import React, { useState, useEffect, useRef } from "react";
import "./WorkspacePicker.css";

/**
 * WorkspacePicker — dropdown-style picker showing the workspace hierarchy
 * as an indented tree with a search filter. Intended to replace flat
 * `<select>` elements wherever the user needs to pick one workspace from a
 * potentially-large, nested list.
 *
 * Shape of each entry in `workspaces`:
 *   { id, name, depth, parentId }
 * Depth is used only for visual indentation; the list is expected to be
 * pre-sorted by the caller (typically in the tree-walk order the backend
 * returns from /videri/workspaces).
 *
 * Props:
 *   workspaces        array of workspace entries (see shape above)
 *   selectedId        currently-selected workspace id (or null)
 *   onSelect(ws)      called with the clicked workspace object
 *   placeholder       search input placeholder (default "Search workspaces...")
 *   emptyLabel        trigger button label when nothing selected
 *   disabled          optional; grays out the trigger
 *   className         optional extra class for the root
 */
export default function WorkspacePicker({
  workspaces = [],
  selectedId,
  onSelect,
  placeholder = "Search workspaces...",
  emptyLabel = "Select workspace",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Close when clicking outside the picker
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Auto-focus search when opened so the user can just start typing
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const selected = workspaces.find((w) => w.id === selectedId);
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? workspaces
    : workspaces.filter((w) => (w.name || "").toLowerCase().includes(q));

  const pick = (ws) => {
    onSelect(ws);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={`wp-picker ${className}`} ref={rootRef}>
      <button
        type="button"
        className="wp-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="wp-trigger-label" title={selected?.name || emptyLabel}>
          {selected?.name || emptyLabel}
        </span>
        <span className="wp-trigger-caret">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="wp-dropdown" role="listbox">
          <input
            ref={searchRef}
            className="wp-search"
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="wp-list">
            {filtered.map((ws) => (
              <div
                key={ws.id}
                role="option"
                aria-selected={selectedId === ws.id}
                className={`wp-list-item ${selectedId === ws.id ? "selected" : ""}`}
                onClick={() => pick(ws)}
                style={{ paddingLeft: `${14 + (ws.depth || 0) * 16}px` }}
              >
                {(ws.depth || 0) > 0 && <span className="wp-tree-indent">{"\u2514"}</span>}
                <span className="wp-item-name">{ws.name}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="wp-empty">No workspaces found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
